'use client'

import { z } from 'zod'

import { studioSession } from '@/features/studio-renew/services/studio-session'
import {
  creatorQuestClaimSchema,
  creatorQuestSchema,
} from '@/features/studio-renew/schemas/creator-quest.schema'
import { ApiError, api } from '@/features/veluvit/services/api'

const tracks = z.enum(['character', 'world', 'command', 'status'])
const targetsSchema = z.strictObject({
  targets: z.partialRecord(tracks, z.string().min(1)),
})
const claimSchema = z.strictObject({
  ids: z
    .array(z.string().min(1))
    .min(1)
    .refine((ids) => new Set(ids).size === ids.length),
  version: z.number().int().nonnegative().optional(),
  requestId: z.string().regex(/^[\w-]{8,100}$/),
})

function checked<T>(schema: z.ZodType<T>, value: unknown): T {
  const parsed = schema.safeParse(value)
  if (!parsed.success)
    throw new ApiError(
      '제작 퀘스트의 서버 응답을 확인하지 못했어요. 진행을 다시 확인해 주세요.',
      502,
      'INVALID_CREATOR_QUEST'
    )
  return parsed.data
}

export async function readCreatorQuest() {
  const session = studioSession()
  const response = await api<unknown>('/creator/me/quest/')
  session.assertCurrent()
  return checked(creatorQuestSchema, response)
}

export async function selectCreatorQuestTargets(
  input: z.input<typeof targetsSchema>
) {
  const payload = targetsSchema.parse(input)
  const session = studioSession()
  const response = await api<unknown>('/creator/me/quest/', payload, 'PATCH')
  session.assertCurrent()
  return checked(creatorQuestSchema, response)
}

/** 서버가 반환한 현재 단계 ID/version만 수령한다. 동일 요청 재시도에는 동일 requestId를 전달한다. */
export async function claimCreatorQuest(input: z.input<typeof claimSchema>) {
  const payload = claimSchema.parse(input)
  const session = studioSession()
  const response = await api<unknown>(
    '/creator/me/quest/claim/',
    payload,
    'POST'
  )
  session.assertCurrent()
  const result = checked(creatorQuestClaimSchema, response)
  if (result.requestId !== payload.requestId)
    throw new Error(
      '보상 수령 요청과 결과가 일치하지 않아요. 기존 요청을 다시 확인해 주세요.'
    )
  return result
}
